















































































































import { get } from "lodash";
import { Vue, Component } from "vue-property-decorator";
import {
  SvgIcon,
  Tabs,
  TabPane,
  Button,
  Message,
  Breadcrumb,
} from "@brankas/web-ui";
import { namespace } from "vuex-class";

import { KYCStatus, KYCStatusMap } from "@/proto/identity/identity/all_pb";
import { ClientInfoCard } from "@/components";
import { ClientInfo } from "@/types";
import { FeatureToggles } from "@/utils/featureToggles";

import TransactionList from "@/pagesCom/ClientInfo/TransactionList.vue";
import UserList from "@/pagesCom/ClientInfo/UserList.vue";
import IntegrationList from "@/pagesCom/ClientInfo/IntegrationList.vue";
import PromoteLiveDialog from "@/pagesCom/ClientInfo/dialogs/PromoteLiveDialog.vue";
import RevokeLiveDialog from "@/pagesCom/ClientInfo/dialogs/RevokeLiveDialog.vue";
import RejectApplicationDialog from "@/pagesCom/ClientInfo/dialogs/RejectApplicationDialog.vue";
import RejectSuccessSummary from "@/pagesCom/ClientInfo/summary/RejectSuccess.vue";

const clientModule = namespace("client");
const configModule = namespace("config");

@Component({
  components: {
    Breadcrumb,
    Message,
    SvgIcon,
    Tabs,
    TabPane,
    Button,
    ClientInfoCard,
    TransactionList,
    UserList,
    IntegrationList,
    PromoteLiveDialog,
    RevokeLiveDialog,
    RejectApplicationDialog,
    RejectSuccessSummary,
  },
})
export default class ClientInfoPage extends Vue {
  @configModule.Getter("getToggle") getToggle!: (
    key: FeatureToggles
  ) => boolean;

  @clientModule.State("currentClient") clientInfo!: ClientInfo;
  @clientModule.State("currentTab") currentTab!: string;
  @clientModule.Mutation("setCurrentTab") setCurrentTab!: (
    tabName: string
  ) => void;
  @clientModule.Action("getClientInfo") getClientInfo!: (
    clientId: string
  ) => void;
  @clientModule.Action("checkLiveAccessPermission")
  checkLiveAccessPermission!: () => boolean;
  @clientModule.Action("updateLiveAccess") updateLiveAccess!: ({
    clientId,
    isLive,
    reason,
  }: {
    clientId: string;
    isLive: boolean;
    reason: string | null;
  }) => void;
  @clientModule.Action("updateKycStatus") updateKycStatus!: ({
    clientId,
    kycStatus,
  }: {
    clientId: string;
    kycStatus: KYCStatusMap[keyof KYCStatusMap];
  }) => void;
  @clientModule.Action("rejectKYCApplication") rejectKYCApplication!: (
    clientId: string
  ) => void;

  loading = false;
  allowToPromote = false;
  showPromoteLiveDialog = false;
  showRevokeLiveDialog = false;
  showRejectDialog = false;
  showRejectSummary = false;
  errorMsg = "";

  async _toggleLiveAccess(isLive: boolean, reason: string | null = null) {
    this.loading = true;
    this.errorMsg = "";
    try {
      await this.updateLiveAccess({ clientId: this.clientId, isLive, reason });
      // if promote change kyc status to APPROVE, if revoke change kyc status to NOT_APPLYING
      await this.updateKycStatus({
        clientId: this.clientId,
        kycStatus: isLive ? KYCStatus.APPROVE : KYCStatus.NOT_APPLYING,
      });

      // Re-fetching client info
      await this._fetchClientInfo();
    } catch (err) {
      this.errorMsg = err.message;
    } finally {
      this.loading = false;
      this.showPromoteLiveDialog = false;
      this.showRevokeLiveDialog = false;
    }
  }

  _fetchClientInfo() {
    this.getClientInfo(this.clientId);
  }

  async handleRejectApplication() {
    this.loading = true;
    this.errorMsg = "";
    try {
      await this.rejectKYCApplication(this.clientId);

      this.showRejectDialog = false;
      this.showRejectSummary = true;
    } catch (err) {
      this.errorMsg = err.message;
    } finally {
      this.loading = false;
      this.showRejectDialog = false;
    }
  }

  get shouldShowUserListTab() {
    return this.getToggle(FeatureToggles.USER_LIST);
  }

  get showSummary(): boolean {
    return this.showRejectSummary;
  }

  get allowRequestResubmit(): boolean {
    return (
      this.allowToPromote &&
      this.getToggle(FeatureToggles.RESUBMIT_DOCS) &&
      this.clientInfo &&
      this.clientInfo.kycStatus === KYCStatus.PENDING
    );
  }

  get allowRejectApplication(): boolean {
    return (
      this.allowToPromote &&
      this.getToggle(FeatureToggles.REJECT_KYC) &&
      this.clientInfo &&
      this.clientInfo.kycStatus === KYCStatus.PENDING
    );
  }

  get navigations() {
    return [
      {
        label: "CLIENT LIST",
        link: "/",
      },
      {
        label: this.clientInfo?.name || "",
      },
    ];
  }

  get selectedTab() {
    return this.currentTab;
  }

  set selectedTab(name: string) {
    this.setCurrentTab(name);
  }

  get clientId() {
    return get(this.$route.params, "clientId");
  }

  get hasLiveAccess(): boolean {
    return this.clientInfo ? !!this.clientInfo.isLive : false;
  }

  get isRequesting(): boolean {
    if (!this.allowToPromote) return false;
    return this.clientInfo
      ? this.clientInfo.kycStatus === KYCStatus.PENDING
      : false;
  }

  async mounted() {
    // Check user permission if it's allow to promote
    this.allowToPromote = await this.checkLiveAccessPermission();
  }

  async promoteToLive(reason: string | null = null) {
    await this._toggleLiveAccess(true, reason);
  }

  async revokeLiveAccess(reason: string | null = null) {
    await this._toggleLiveAccess(false, reason);
  }
}
